/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseShipmentPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/shipment 成員出貨（S3 spec §8.2）— 出貨計畫 form + 現況
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  RBAC gate：marketplace.shipment_documents.upload（PERMISSIONS.shipmentManage）。
 *  欄位映射 → 後端 PlanV12ShipmentDto：
 *    planned_ship_at（必填 ISO8601）/ logistics_company / truck_type /
 *    truck_contact_name（運転手）/ truck_contact_phone / truck_notes / truck_info_pending。
 *  後端目前無獨立 attachment endpoint；磅單檔案以 truck_notes 文字描述參照，
 *  正式 evidence 上傳由 /shipments/:id/evidence（另切片）處理。
 */

import type { ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { getCaseShipment, planShipment, type PlanShipmentDto } from '../api/shipments'
import { ApiError } from '../api/client'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'

interface PageProps {
  t: (key: string) => string
}

const formSchema = z.object({
  plannedShipAt: z.string().trim().min(1),
  plannedArrivalAt: z.string().trim().optional().default(''),
  logisticsCompany: z.string().trim().optional().default(''),
  truckType: z.string().trim().optional().default(''),
  truckContactName: z.string().trim().optional().default(''),
  truckContactPhone: z.string().trim().optional().default(''),
  truckNotes: z.string().trim().optional().default(''),
  truckInfoPending: z.boolean().default(false),
})

type ShipmentFormValues = z.input<typeof formSchema>

/** `datetime-local` 值 → ISO8601（後端 DateTime<Utc>）；空字串回 null。 */
function toIso(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** 表單值 → 後端 PlanV12ShipmentDto。 */
function toDto(values: ShipmentFormValues): PlanShipmentDto {
  return {
    planned_ship_at: toIso(values.plannedShipAt) ?? new Date().toISOString(),
    planned_arrival_at: toIso(values.plannedArrivalAt ?? '') ?? undefined,
    logistics_company: values.logisticsCompany?.trim() || undefined,
    truck_type: values.truckType?.trim() || undefined,
    truck_contact_name: values.truckContactName?.trim() || undefined,
    truck_contact_phone: values.truckContactPhone?.trim() || undefined,
    truck_notes: values.truckNotes?.trim() || undefined,
    truck_info_pending: values.truckInfoPending ?? false,
    metadata_json: undefined,
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
}

export function CaseShipmentPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['case', caseId, 'shipment'],
    queryFn: () => getCaseShipment(caseId),
    enabled: caseId !== '',
    retry: false,
  })

  const form = useForm<ShipmentFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      plannedShipAt: '',
      plannedArrivalAt: '',
      logisticsCompany: '',
      truckType: '',
      truckContactName: '',
      truckContactPhone: '',
      truckNotes: '',
      truckInfoPending: false,
    },
  })

  const mutation = useMutation({
    mutationFn: (values: ShipmentFormValues) => planShipment(caseId, toDto(values)),
    onSuccess: () => {
      toast.success(t('shipment.plan.success'))
      void queryClient.invalidateQueries({ queryKey: ['case', caseId, 'shipment'] })
    },
    onError: (error) => {
      const detail = error instanceof ApiError ? `（${error.message}）` : ''
      toast.error(`${t('shipment.plan.error')}${detail}`)
    },
  })

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values))

  // 404 = 尚未建立出貨計畫，屬正常初始狀態，不視為錯誤。
  const notFound = query.error instanceof ApiError && query.error.status === 404
  const shipment = query.data?.shipment

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('shipment.current.title')}</CardTitle>
          {shipment ? <Badge variant="secondary">{shipment.status}</Badge> : null}
        </CardHeader>
        <CardContent>
          {query.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : shipment ? (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              <Field label={t('shipment.field.shipmentNo')} value={shipment.shipment_no} />
              <Field
                label={t('shipment.field.plannedShipAt')}
                value={formatDate(shipment.planned_ship_at)}
              />
              <Field
                label={t('shipment.field.logisticsCompany')}
                value={shipment.logistics_company ?? '—'}
              />
              <Field label={t('shipment.field.truckType')} value={shipment.truck_type ?? '—'} />
              <Field
                label={t('shipment.field.driver')}
                value={shipment.truck_contact_name ?? '—'}
              />
              <Field
                label={t('shipment.field.driverPhone')}
                value={shipment.truck_contact_phone ?? '—'}
              />
            </dl>
          ) : (
            <p className="text-sm text-muted-foreground">
              {notFound ? t('shipment.current.empty') : t('shipment.current.loadError')}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('shipment.plan.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="plannedShipAt">{t('shipment.field.plannedShipAt')}</Label>
                <Input
                  id="plannedShipAt"
                  type="datetime-local"
                  aria-invalid={!!form.formState.errors.plannedShipAt}
                  {...form.register('plannedShipAt')}
                />
                {form.formState.errors.plannedShipAt ? (
                  <p className="text-xs text-destructive">{t('common.required')}</p>
                ) : null}
              </div>
              <div className="space-y-1">
                <Label htmlFor="plannedArrivalAt">{t('shipment.field.plannedArrivalAt')}</Label>
                <Input
                  id="plannedArrivalAt"
                  type="datetime-local"
                  {...form.register('plannedArrivalAt')}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="logisticsCompany">{t('shipment.field.logisticsCompany')}</Label>
                <Input id="logisticsCompany" {...form.register('logisticsCompany')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="truckType">{t('shipment.field.truckType')}</Label>
                <Input id="truckType" {...form.register('truckType')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="truckContactName">{t('shipment.field.driver')}</Label>
                <Input id="truckContactName" {...form.register('truckContactName')} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="truckContactPhone">{t('shipment.field.driverPhone')}</Label>
                <Input id="truckContactPhone" {...form.register('truckContactPhone')} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="truckNotes">{t('shipment.field.scaleRef')}</Label>
              <Input id="truckNotes" {...form.register('truckNotes')} />
              <p className="text-xs text-muted-foreground">{t('shipment.field.scaleRefHint')}</p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" {...form.register('truckInfoPending')} />
              {t('shipment.field.truckInfoPending')}
            </label>
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? t('shipment.plan.submitting') : t('shipment.plan.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  )
}
